<template>
  <v-dialog v-model="internalValue" fullscreen hide-overlay transition="dialog-bottom-transition">
    <v-card class="chat-ia-container d-flex flex-column">
      <v-toolbar color="#0B7285" dark flat dense class="chat-ia-toolbar">
        <v-btn icon @click="fechar">
          <v-icon>close</v-icon>
        </v-btn>
        <v-toolbar-title class="font-weight-bold text-subtitle-1">
          <v-icon left small>smart_toy</v-icon>
          {{ modo === 'editar' ? 'Editar Relatório com IA' : 'Criar Relatório com IA' }}
        </v-toolbar-title>

        <v-spacer></v-spacer>

        <div class="d-flex align-center toolbar-configs">
          <div class="d-flex align-center temp-control">
            <span class="text-caption white--text temp-label">Temp: {{ temperature }}</span>
            <v-slider v-model="temperature" min="0" max="2" step="0.1" hide-details dense color="white"
              track-color="blue-grey darken-2" thumb-label class="temp-slider"></v-slider>
          </div>
        </div>
      </v-toolbar>

      <div class="d-flex flex-grow-1 panels-wrapper">
        <div class="chat-panel d-flex flex-column">
          <div class="messages-container flex-grow-1 pa-4" ref="messagesContainer">
            <div v-for="msg in messagesView" :key="msg.id" class="message-wrapper d-flex mb-4"
              :class="{ 'justify-end': msg.role === 'user' }">
              <div v-if="msg.role === 'model'"
                class="avatar ai-avatar mr-2 d-flex align-center justify-center rounded-circle">
                <v-icon dark small color="#0B7285">smart_toy</v-icon>
              </div>

              <div class="message-content d-flex flex-column" :class="{ 'align-end': msg.role === 'user' }">
                <div class="message-bubble pa-3 elevation-1" :class="{
                  'user-bubble': msg.role === 'user',
                  'ai-bubble': msg.role === 'model'
                }">
                  <div v-if="msg.isHtmlContext"
                    class="html-context-message text-caption grey--text mb-2 pb-2 border-bottom">
                    <v-icon small left color="grey">description</v-icon>
                    HTML do relatório anexado como contexto
                  </div>
                  <div v-if="msg.html" class="message-text" v-html="msg.html"></div>
                  <div v-if="msg.images.length" class="message-images mt-2">
                    <button v-for="(image, imageIndex) in msg.images" :key="image.id || `${msg.id}-${imageIndex}`"
                      type="button" class="message-image-button" @click="abrirPreviewImagens(msg.images, imageIndex)">
                      <img :src="image.url" :alt="image.name || `Imagem ${imageIndex + 1}`" class="message-image-thumb">
                    </button>
                  </div>
                </div>
              </div>

              <div v-if="msg.role === 'user'"
                class="avatar user-avatar ml-2 d-flex align-center justify-center rounded-circle">
                <v-icon dark small>person</v-icon>
              </div>
            </div>

            <div v-if="digitando" class="message-wrapper d-flex mb-4">
              <div class="avatar ai-avatar mr-2 d-flex align-center justify-center rounded-circle">
                <v-icon dark small color="#0B7285">smart_toy</v-icon>
              </div>
              <div class="message-bubble ai-bubble pa-3 elevation-1 d-flex align-center">
                <span class="typing-dot"></span>
                <span class="typing-dot"></span>
                <span class="typing-dot"></span>
              </div>
            </div>
          </div>

          <div class="input-container pa-3">
            <input ref="imageInput" type="file" accept="image/*" multiple class="d-none" @change="onSelecionarImagens">
            <v-textarea ref="messageTextarea" v-model="inputTexto"
              placeholder="Digite aqui... (Enter para enviar, Shift+Enter para nova linha)" outlined dense hide-details
              auto-grow rows="2" max-rows="5" @keydown="handleKeydown">
              <template v-slot:append>
                <v-btn icon color="#0B7285" @click="abrirSeletorImagens"
                  :disabled="digitando || imagensPendentes.length >= maxImagensPorMensagem">
                  <v-icon>attach_file</v-icon>
                </v-btn>
                <v-btn icon color="#0B7285" @click="enviarMensagem" :disabled="!canSendMessage || digitando">
                  <v-icon>send</v-icon>
                </v-btn>
              </template>
            </v-textarea>

            <div v-if="imagensPendentes.length" class="pending-images mt-3">
              <div class="text-caption grey--text text--darken-1 mb-2">
                Imagens anexadas ({{ imagensPendentes.length }}/{{ maxImagensPorMensagem }})
              </div>
              <div class="pending-images-grid">
                <div v-for="(image, imageIndex) in imagensPendentes" :key="image.id" class="pending-image-card">
                  <button type="button" class="pending-image-button"
                    @click="abrirPreviewImagens(imagensPendentes, imageIndex)">
                    <img :src="image.url" :alt="image.name || `Imagem ${imageIndex + 1}`" class="pending-image-thumb">
                  </button>
                  <div class="pending-image-actions d-flex align-center justify-space-between mt-1">
                    <span class="pending-image-name text-caption grey--text text--darken-1">
                      {{ image.name || `Imagem ${imageIndex + 1}` }}
                    </span>
                    <v-btn icon x-small color="grey darken-1" @click="removerImagemPendente(image.id)">
                      <v-icon small>close</v-icon>
                    </v-btn>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="preview-panel d-flex flex-column">
          <div class="preview-header py-2 px-4 d-flex align-center justify-space-between">
            <span class="font-weight-bold grey--text text--darken-2">
              <v-icon left size="20">visibility</v-icon> Preview em Tempo Real
            </span>
            <div class="d-flex align-center">
              <v-chip v-if="temHtml && !digitando" color="green" text-color="white" small class="mr-3 font-weight-bold">
                <v-icon left size="16">check_circle</v-icon> Pronto
              </v-chip>
              <v-chip v-if="digitando" color="orange" text-color="white" small class="mr-3 font-weight-bold">
                <v-icon left size="16">update</v-icon> Gerando...
              </v-chip>
            </div>
          </div>

          <div class="preview-content flex-grow-1 position-relative pa-2">
            <div v-if="!htmlExtraido" class="empty-preview d-flex flex-column align-center justify-center">
              <v-icon size="64" color="grey lighten-2">integration_instructions</v-icon>
              <h3 class="grey--text text--darken-1 mt-4">Nenhum relatório gerado ainda</h3>
              <p class="grey--text text-center mt-2" style="max-width: 300px">
                Inicie a conversa no chat para que a IA crie o relatório. O resultado aparecerá aqui.
              </p>
            </div>

            <iframe v-else ref="iframePreview" class="analytics-iframe elevation-2" :srcdoc="previewSrcdoc"
              sandbox="allow-scripts allow-downloads" frameborder="0" @load="onIframeLoad"></iframe>
          </div>

          <div class="actions-panel pa-3 d-flex justify-end pr-5">
            <div v-if="versions.length" class="version-controls d-flex align-center mr-auto">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon small color="#0B7285" v-bind="attrs" v-on="on" :disabled="!canNavigatePreviousVersion"
                    @click="navegarVersao(-1)">
                    <v-icon>chevron_left</v-icon>
                  </v-btn>
                </template>
                <span>Versão anterior</span>
              </v-tooltip>

              <div class="version-status text-caption grey--text text--darken-2 mx-2">
                {{ versionStatusText }}
              </div>

              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon small color="#0B7285" v-bind="attrs" v-on="on" :disabled="!canNavigateNextVersion"
                    @click="navegarVersao(1)">
                    <v-icon>chevron_right</v-icon>
                  </v-btn>
                </template>
                <span>Próxima versão</span>
              </v-tooltip>
            </div>

            <v-btn text color="grey darken-1" @click="fechar" class="mr-2">Cancelar</v-btn>
            <v-btn outlined color="#0B7285" class="mr-3" @click="forcarAtualizarPreview"
              :disabled="!temHtml || digitando">
              <v-icon left>autorenew</v-icon> Atualizar Preview
            </v-btn>
            <v-btn outlined color="#0B7285" class="mr-3" @click="baixarHtmlAtual" :disabled="!temHtml || digitando">
              <v-icon left>download</v-icon> Download HTML
            </v-btn>
            <v-btn color="#0B7285" dark @click="finalizarESalvar" :loading="salvando" :disabled="!temHtml || digitando">
              <v-icon left>save</v-icon> Finalizar e Salvar
            </v-btn>
          </div>
        </div>
      </div>

      <v-dialog v-model="dialogErroPreview" max-width="760">
        <v-card>
          <v-card-title class="text-h6 font-weight-bold">
            Erro detectado no preview
          </v-card-title>
          <v-card-text class="pb-2">
            Ocorreu um erro que está impedindo o funcionamento adequado do relatório no preview. Deseja solicitar para a
            IA
            tentar corrigir isso automaticamente?
            <div v-if="erroPreviewPendente" class="mt-3">
              <v-btn text small color="#0B7285" class="erro-preview-toggle px-0" @click="toggleDetalhesErroPreview">
                <v-icon left small>
                  {{ detalhesErroPreviewExpandidos ? "expand_less" : "expand_more" }}
                </v-icon>
                {{ detalhesErroPreviewExpandidos ? "Ocultar erro" : "Visualizar erro" }}
              </v-btn>

              <v-expand-transition>
                <div v-if="detalhesErroPreviewExpandidos" class="erro-preview-box mt-2">
                  <pre class="erro-preview-text">{{ erroPreviewDetalhadoTexto }}</pre>
                </div>
              </v-expand-transition>
            </div>
          </v-card-text>
          <v-card-actions class="px-4 pb-4">
            <v-spacer></v-spacer>
            <v-btn text color="grey darken-1" @click="fecharDialogErroPreview">
              Agora não
            </v-btn>
            <v-btn color="#0B7285" dark :loading="corrigindoErroPreview" :disabled="digitando"
              @click="solicitarCorrecaoErroPreview">
              Solicitar correção com IA
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="dialogPreviewImagem" max-width="960">
        <v-card class="image-preview-dialog">
          <div class="d-flex align-center justify-space-between px-4 py-3">
            <div class="font-weight-medium">
              {{ imagemPreviewAtual ? (imagemPreviewAtual.name || `Imagem ${previewImageIndex + 1}`) : '' }}
            </div>
            <div class="d-flex align-center">
              <v-btn icon class="mr-1" :disabled="!canPreviewPreviousImage" @click="navegarPreviewImagem(-1)">
                <v-icon>chevron_left</v-icon>
              </v-btn>
              <v-btn icon class="mr-2" :disabled="!canPreviewNextImage" @click="navegarPreviewImagem(1)">
                <v-icon>chevron_right</v-icon>
              </v-btn>
              <v-btn icon @click="fecharPreviewImagem">
                <v-icon>close</v-icon>
              </v-btn>
            </div>
          </div>
          <v-divider></v-divider>
          <div class="image-preview-body d-flex align-center justify-center pa-4">
            <img v-if="imagemPreviewAtual" :src="imagemPreviewAtual.url"
              :alt="imagemPreviewAtual.name || 'Imagem anexada'" class="image-preview-full">
          </div>
        </v-card>
      </v-dialog>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from "axios";
import { urlApi, showError } from "@/global";
import { clearIframePayloadSyncTimer, scheduleIframePayloadSync } from "./iframePayloadSync";
import {
  createIframeReadyBootstrap,
  IFRAME_READY_MESSAGE_TYPE,
  injectScriptIntoHtml
} from "./iframeReadyBridge";

export default {
  name: "chatIA",
  props: {
    value: {
      type: Boolean,
      default: false
    },
    modo: {
      type: String,
      default: "criar"
    },
    htmlInicial: {
      type: String,
      default: ""
    },
    tituloRelatorio: {
      type: String,
      default: ""
    },
    nomeOriginal: {
      type: String,
      default: ""
    },
    jsonDados: {
      type: Object,
      default: () => null
    }
  },
  data() {
    return {
      internalValue: this.value,
      inputTexto: "",
      messages: [],
      versions: [],
      imagensPendentes: [],
      digitando: false,
      salvando: false,
      temperature: 1.0,
      htmlExtraido: "",
      iframeCarregado: false,
      iframeProntoParaDados: false,
      reenvioPreviewTimer: null,
      reenvioPreviewSequencia: 0,
      abortController: null,
      nextMessageId: 1,
      nextVersionId: 1,
      currentVersionId: null,
      previewMonitorToken: 0,
      textareaPasteEl: null,
      dialogErroPreview: false,
      corrigindoErroPreview: false,
      erroPreviewPendente: null,
      detalhesErroPreviewExpandidos: false,
      assinaturasErroPreviewJaProcessadas: [],
      maxImagensPorMensagem: 5,
      maxDimensaoImagem: 1600,
      maxBytesImagem: 850 * 1024,
      nextImageId: 1,
      dialogPreviewImagem: false,
      previewImages: [],
      previewImageIndex: 0,
    };
  },
  computed: {
    messagesView() {
      return this.messages
        .filter(msg => this.mensagemTemConteudoVisivel(msg) && !msg.hideFromChat)
        .map(msg => {
          const textoBruto = this.obterTextoMensagem(msg);
          const isHtmlContext = !!msg.isHtmlContext;
          let textoLimpo = textoBruto;

          if (msg.role === "model") {
            textoLimpo = textoBruto.replace(/```html[\s\S]*?```/gi, "\n[HTML renderizado no painel de Preview]\n");
            textoLimpo = textoLimpo.replace(/```[\s\S]*?```/gi, "\n[Código renderizado no painel de Preview]\n");
          }

          return {
            id: msg.id,
            role: msg.role,
            text: textoLimpo,
            html: this.textoParaHtml(textoLimpo, isHtmlContext),
            isHtmlContext,
            images: this.obterImagensMensagem(msg),
          };
        });
    },
    canSendMessage() {
      return !!this.inputTexto.trim() || this.imagensPendentes.length > 0;
    },
    temHtml() {
      return !!this.htmlExtraido;
    },
    previewSrcdoc() {
      return this.temHtml
        ? this.gerarHtmlPreviewInstrumentado(this.htmlExtraido)
        : "";
    },
    currentVersionIndex() {
      return this.versions.findIndex(version => version.id === this.currentVersionId);
    },
    currentVersion() {
      return this.currentVersionIndex >= 0
        ? this.versions[this.currentVersionIndex]
        : null;
    },
    canNavigatePreviousVersion() {
      return this.currentVersionIndex > 0 && !this.digitando;
    },
    canNavigateNextVersion() {
      return this.currentVersionIndex >= 0
        && this.currentVersionIndex < this.versions.length - 1
        && !this.digitando;
    },
    versionStatusText() {
      if (!this.versions.length || this.currentVersionIndex < 0) {
        return "Nenhuma versão";
      }

      return `Versão ${this.currentVersionIndex + 1} de ${this.versions.length}`;
    },
    erroPreviewDetalhadoTexto() {
      return this.montarDetalhesErroPreview(this.erroPreviewPendente);
    },
    imagemPreviewAtual() {
      return this.previewImages[this.previewImageIndex] || null;
    },
    canPreviewPreviousImage() {
      return this.previewImageIndex > 0;
    },
    canPreviewNextImage() {
      return this.previewImageIndex < this.previewImages.length - 1;
    }
  },
  watch: {
    value(val) {
      this.internalValue = val;
      if (val) this.iniciarSessao();
    },
    internalValue(val) {
      this.$emit("input", val);
    },
    jsonDados() {
      if (this.temHtml) {
        this.agendarEnvioDadosParaIframe();
      }
    },
    htmlExtraido() {
      this.iframeCarregado = false;
      this.iframeProntoParaDados = false;
      clearIframePayloadSyncTimer(this, "reenvioPreviewTimer", "reenvioPreviewSequencia");
      this.previewMonitorToken += 1;
      this.resetarEstadoErroPreview();
    }
  },
  mounted() {
    window.addEventListener("message", this.handleWindowMessage);
    window.addEventListener("resize", this.handleViewportChange);
    this.$nextTick(() => {
      this.conectarListenerPaste();
    });
  },
  beforeDestroy() {
    this.abortarStreaming();
    clearIframePayloadSyncTimer(this, "reenvioPreviewTimer", "reenvioPreviewSequencia");
    window.removeEventListener("message", this.handleWindowMessage);
    window.removeEventListener("resize", this.handleViewportChange);
    this.desconectarListenerPaste();
  },
  methods: {
    normalizarHtmlSnapshot(html) {
      return typeof html === "string" ? html : "";
    },

    definirHtmlExtraido(html) {
      this.htmlExtraido = this.normalizarHtmlSnapshot(html);
    },

    obterTextoMensagem(message) {
      if (!message || !Array.isArray(message.parts) || !message.parts.length) return "";
      return message.parts
        .filter(part => part && typeof part.text === "string")
        .map(part => part.text)
        .join("");
    },

    obterImagensMensagem(message) {
      if (!message || !Array.isArray(message.parts)) return [];

      return message.parts
        .map((part, index) => {
          const inlineData = part
            ? (part.inlineData || part.inline_data || null)
            : null;
          const mimeType = inlineData
            ? (inlineData.mimeType || inlineData.mime_type || "")
            : "";
          const data = inlineData && typeof inlineData.data === "string"
            ? inlineData.data
            : "";

          if (!inlineData || !mimeType || !data) return null;
          if (!String(mimeType).startsWith("image/")) return null;

          return {
            id: part.id || `${message.id || "msg"}-img-${index}`,
            mimeType,
            base64: data,
            name: part.name || `Imagem ${index + 1}`,
            url: `data:${mimeType};base64,${data}`,
          };
        })
        .filter(Boolean);
    },

    mensagemTemConteudoVisivel(message) {
      return !!this.obterTextoMensagem(message) || this.obterImagensMensagem(message).length > 0;
    },

    criarMensagem({
      role,
      text = "",
      parts = null,
      isLocal = false,
      isHtmlContext = false,
      hideFromChat = false,
    }) {
      return {
        id: this.nextMessageId++,
        role,
        parts: Array.isArray(parts) && parts.length
          ? this.clonarMensagens(parts)
          : [{ text }],
        isLocal,
        isHtmlContext,
        hideFromChat,
      };
    },

    criarIdImagem() {
      return `img-${this.nextImageId++}`;
    },

    notificarErro(msg) {
      if (this.$toasted && this.$toasted.global && this.$toasted.global.defaultError) {
        this.$toasted.global.defaultError({ msg });
      }
    },

    conectarListenerPaste() {
      const textarea = this.$refs.messageTextarea
        && this.$refs.messageTextarea.$el
        ? this.$refs.messageTextarea.$el.querySelector("textarea")
        : null;

      if (!textarea || this.textareaPasteEl === textarea) return;

      this.desconectarListenerPaste();
      this.textareaPasteEl = textarea;
      textarea.addEventListener("paste", this.handlePaste);
    },

    desconectarListenerPaste() {
      if (this.textareaPasteEl) {
        this.textareaPasteEl.removeEventListener("paste", this.handlePaste);
        this.textareaPasteEl = null;
      }
    },

    abrirSeletorImagens() {
      if (this.digitando || this.imagensPendentes.length >= this.maxImagensPorMensagem) return;
      if (this.$refs.imageInput) {
        this.$refs.imageInput.click();
      }
    },

    async onSelecionarImagens(event) {
      const files = Array.from((event && event.target && event.target.files) || []);
      await this.adicionarArquivosImagem(files);
      if (event && event.target) {
        event.target.value = "";
      }
    },

    async handlePaste(event) {
      const clipboardItems = event && event.clipboardData && event.clipboardData.items
        ? Array.from(event.clipboardData.items)
        : [];

      const imageFiles = clipboardItems
        .filter(item => item && item.kind === "file" && item.type && item.type.startsWith("image/"))
        .map(item => item.getAsFile())
        .filter(Boolean);

      if (!imageFiles.length) return;

      event.preventDefault();
      await this.adicionarArquivosImagem(imageFiles);
    },

    async adicionarArquivosImagem(files) {
      if (!Array.isArray(files) || !files.length) return;

      const espacosDisponiveis = this.maxImagensPorMensagem - this.imagensPendentes.length;
      if (espacosDisponiveis <= 0) {
        this.notificarErro(`Você pode anexar até ${this.maxImagensPorMensagem} imagens por mensagem.`);
        return;
      }

      const arquivosValidos = files.filter(file => file && file.type && file.type.startsWith("image/"));
      if (!arquivosValidos.length) {
        this.notificarErro("Selecione apenas arquivos de imagem.");
        return;
      }

      if (arquivosValidos.length > espacosDisponiveis) {
        this.notificarErro(`A mensagem aceita no máximo ${this.maxImagensPorMensagem} imagens.`);
      }

      const arquivosSelecionados = arquivosValidos.slice(0, espacosDisponiveis);
      const imagensCriadas = await Promise.all(arquivosSelecionados.map(file => this.criarImagemPendente(file)));
      this.imagensPendentes.push(...imagensCriadas.filter(Boolean));
    },

    lerArquivoComoDataUrl(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : "");
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },

    carregarImagem(dataUrl) {
      return new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = reject;
        image.src = dataUrl;
      });
    },

    calcularBytesBase64(base64) {
      if (!base64) return 0;
      const padding = (base64.match(/=+$/) || [""])[0].length;
      return Math.floor((base64.length * 3) / 4) - padding;
    },

    reduzirDimensoesImagem(width, height, maxDimension) {
      if (!width || !height || (width <= maxDimension && height <= maxDimension)) {
        return { width, height };
      }

      const scale = Math.min(maxDimension / width, maxDimension / height);
      return {
        width: Math.max(1, Math.round(width * scale)),
        height: Math.max(1, Math.round(height * scale)),
      };
    },

    async otimizarImagemArquivo(file) {
      const dataUrlOriginal = await this.lerArquivoComoDataUrl(file);
      const base64Original = dataUrlOriginal.includes(",") ? dataUrlOriginal.split(",")[1] : "";

      if (this.calcularBytesBase64(base64Original) <= this.maxBytesImagem) {
        return {
          mimeType: file.type || "image/png",
          base64: base64Original,
          url: dataUrlOriginal,
        };
      }

      const image = await this.carregarImagem(dataUrlOriginal);
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const dimensoes = this.reduzirDimensoesImagem(
        image.naturalWidth || image.width,
        image.naturalHeight || image.height,
        this.maxDimensaoImagem
      );

      canvas.width = dimensoes.width;
      canvas.height = dimensoes.height;
      ctx.drawImage(image, 0, 0, dimensoes.width, dimensoes.height);

      const mimeTypePreferido = "image/webp";
      const qualityLevels = [0.9, 0.82, 0.74, 0.66, 0.58, 0.5];
      let melhorDataUrl = "";
      let melhorMimeType = mimeTypePreferido;

      qualityLevels.some(quality => {
        const tentativa = canvas.toDataURL(mimeTypePreferido, quality);
        const base64Tentativa = tentativa.split(",")[1] || "";
        melhorDataUrl = tentativa;
        melhorMimeType = tentativa.substring(5, tentativa.indexOf(";")) || mimeTypePreferido;
        return this.calcularBytesBase64(base64Tentativa) <= this.maxBytesImagem;
      });

      const base64 = melhorDataUrl.includes(",") ? melhorDataUrl.split(",")[1] : base64Original;
      return {
        mimeType: melhorMimeType,
        base64,
        url: melhorDataUrl || dataUrlOriginal,
      };
    },

    async criarImagemPendente(file) {
      const imagemOtimizada = await this.otimizarImagemArquivo(file);

      return {
        id: this.criarIdImagem(),
        name: file.name || "imagem",
        mimeType: imagemOtimizada.mimeType,
        base64: imagemOtimizada.base64,
        url: imagemOtimizada.url,
      };
    },

    removerImagemPendente(imageId) {
      this.imagensPendentes = this.imagensPendentes.filter(image => image.id !== imageId);
    },

    limparImagensPendentes() {
      this.imagensPendentes = [];
    },

    criarPartsMensagemUsuario(text, images = []) {
      const parts = [];
      const textoNormalizado = typeof text === "string" ? text.trim() : "";

      if (textoNormalizado) {
        parts.push({ text: textoNormalizado });
      }

      images.forEach(image => {
        if (!image || !image.base64 || !image.mimeType) return;

        parts.push({
          id: image.id,
          name: image.name,
          inlineData: {
            mimeType: image.mimeType,
            data: image.base64,
          }
        });
      });

      return parts;
    },

    abrirPreviewImagens(images, index = 0) {
      if (!Array.isArray(images) || !images.length) return;
      this.previewImages = images.map(image => ({ ...image }));
      this.previewImageIndex = Math.min(Math.max(index, 0), images.length - 1);
      this.dialogPreviewImagem = true;
    },

    navegarPreviewImagem(delta) {
      const nextIndex = this.previewImageIndex + delta;
      if (nextIndex < 0 || nextIndex >= this.previewImages.length) return;
      this.previewImageIndex = nextIndex;
    },

    fecharPreviewImagem() {
      this.dialogPreviewImagem = false;
      this.previewImages = [];
      this.previewImageIndex = 0;
    },

    clonarMensagens(messages) {
      return JSON.parse(JSON.stringify(messages || []));
    },

    criarVersao({ html, sourceMessageId = null }) {
      return {
        id: this.nextVersionId++,
        html: this.normalizarHtmlSnapshot(html),
        sourceMessageId,
        createdAt: new Date().toISOString(),
        messagesSnapshot: this.clonarMensagens(this.messages),
      };
    },

    registrarVersao({ html, sourceMessageId = null }) {
      const normalizedHtml = this.normalizarHtmlSnapshot(html);
      if (!normalizedHtml) return null;

      if (this.currentVersion && this.currentVersion.html === normalizedHtml) {
        this.currentVersionId = this.currentVersion.id;
        return this.currentVersion;
      }

      const version = this.criarVersao({ html: normalizedHtml, sourceMessageId });
      this.versions.push(version);
      this.currentVersionId = version.id;
      return version;
    },

    restaurarVersao(version) {
      if (!version || this.digitando) return;

      this.abortarStreaming();
      this.messages = this.clonarMensagens(version.messagesSnapshot);
      this.currentVersionId = version.id;
      this.definirHtmlExtraido(version.html);

      if (this.$toasted && this.$toasted.global && this.$toasted.global.defaultSuccess) {
        this.$toasted.global.defaultSuccess({ msg: "Versão do relatório restaurada." });
      }

      this.$nextTick(() => {
        if (this.iframeCarregado) {
          this.agendarEnvioDadosParaIframe();
        }
        this.scrollBottom();
      });
    },

    navegarVersao(delta) {
      if (!delta || this.digitando || !this.versions.length) return;

      const targetIndex = this.currentVersionIndex + delta;
      if (targetIndex < 0 || targetIndex >= this.versions.length) return;

      this.restaurarVersao(this.versions[targetIndex]);
    },

    criarTextoContextoHtmlAtual(html) {
      return [
        "HTML atual do relatório que deve ser usado como base para esta solicitação:",
        "```html",
        html,
        "```",
      ].join("\n");
    },

    normalizarPartsParaAPI(parts) {
      if (!Array.isArray(parts) || !parts.length) return [];

      return this.clonarMensagens(parts).filter(part => {
        if (!part || typeof part !== "object") return false;
        if (typeof part.text === "string") return part.text.length > 0 || Object.keys(part).length > 1;
        return Object.keys(part).length > 0;
      });
    },

    prepararMensagensParaAPI(historyMessages = [], currentMessages = []) {
      const mensagens = [];

      const adicionarMensagem = (role, parts) => {
        const partsNormalizados = this.normalizarPartsParaAPI(parts);
        if (!partsNormalizados.length) return;
        mensagens.push({
          role,
          parts: partsNormalizados
        });
      };

      historyMessages.forEach(message => {
        if (!message || message.isLocal || message.isHtmlContext) return;
        adicionarMensagem(message.role, message.parts);
      });

      const htmlAtual = this.normalizarHtmlSnapshot(
        this.currentVersion ? this.currentVersion.html : this.htmlExtraido
      );
      if (htmlAtual && (!this.currentVersion || this.currentVersion.sourceMessageId === null)) {
        adicionarMensagem("user", [{ text: this.criarTextoContextoHtmlAtual(htmlAtual) }]);
      }

      currentMessages.forEach(message => {
        if (!message || message.isLocal || message.isHtmlContext) return;
        adicionarMensagem(message.role, message.parts);
      });

      return mensagens;
    },

    instrumentarScriptsExternosPreview(html) {
      if (!html) return "";

      return html.replace(
        /<script\b([^>]*\bsrc\s*=\s*(['"])(https?:\/\/[^'"]+)\2[^>]*)>/gi,
        (match, attrs, _quote, src) => {
          if (!/chart(?:\.umd|\.min)?(?:\.bundle)?\.js(?:[?#].*)?$/i.test(src)) {
            return match;
          }

          if (/\bcrossorigin\s*=/i.test(attrs)) {
            return match;
          }

          return `<script${attrs} crossorigin="anonymous">`;
        }
      );
    },

    gerarHtmlPreviewInstrumentado(html) {
      const safeHtml = this.instrumentarScriptsExternosPreview(
        this.normalizarHtmlSnapshot(html)
      );
      if (!safeHtml) return "";

      const token = JSON.stringify(this.previewMonitorToken);
      const readyBootstrap = createIframeReadyBootstrap({
        origin: "chatIA-preview-ready",
        token: this.previewMonitorToken,
      });
      const monitorScript = [
        "<script>",
        "(function(){",
        "  if (window.__pregaoPreviewMonitorInstalled) return;",
        "  window.__pregaoPreviewMonitorInstalled = true;",
        `  var token = ${token};`,
        "  function toText(value) {",
        "    try {",
        "      if (value instanceof Error) return value.stack || value.message || String(value);",
        "      if (typeof value === 'string') return value;",
        "      return JSON.stringify(value);",
        "    } catch (e) {",
        "      try { return String(value); } catch (err) { return '[valor-nao-serializavel]'; }",
        "    }",
        "  }",
        "  function send(payload) {",
        "    try {",
        "      window.parent.postMessage(Object.assign({",
        "        tipo: 'pregao-preview-error',",
        "        origem: 'chatIA-preview',",
        "        token: token",
        "      }, payload), '*');",
        "    } catch (e) {}",
        "  }",
        "  function normalizeRuntimeMessage(message, source, lineno, colno, stack, target) {",
        "    var normalized = message || '';",
        "    if ((!normalized || normalized === 'Script error.' || normalized === 'Script error') && target && target.tagName) {",
        "      normalized = 'Falha ao carregar recurso ' + target.tagName.toLowerCase();",
        "      if (target.src) normalized += ': ' + target.src;",
        "      if (target.href) normalized += ': ' + target.href;",
        "    }",
        "    if ((!normalized || normalized === 'Script error.' || normalized === 'Script error') && source) {",
        "      normalized = 'Script error em ' + source;",
        "      if (lineno) normalized += ':' + lineno;",
        "      if (colno) normalized += ':' + colno;",
        "    }",
        "    if ((!normalized || normalized === 'Script error.' || normalized === 'Script error') && stack) {",
        "      normalized = stack;",
        "    }",
        "    return normalized || 'Erro desconhecido no preview.';",
        "  }",
        "  function sendDetailedError(errorType, error, sourceLabel, contextLabel) {",
        "    var stack = error && error.stack ? String(error.stack) : '';",
        "    var message = error && error.message ? String(error.message) : toText(error);",
        "    if (contextLabel) {",
        "      message = contextLabel + ': ' + message;",
        "    }",
        "    send({",
        "      errorType: errorType || 'runtime',",
        "      message: message || 'Erro desconhecido no preview.',",
        "      source: sourceLabel || '',",
        "      lineno: 0,",
        "      colno: 0,",
        "      stack: stack",
        "    });",
        "  }",
        "  function wrapChartMethod(target, methodName) {",
        "    if (!target || typeof target[methodName] !== 'function' || target[methodName].__pregaoWrapped) return;",
        "    var original = target[methodName];",
        "    var wrapped = function() {",
        "      try {",
        "        return original.apply(this, arguments);",
        "      } catch (error) {",
        "        sendDetailedError('chart', error, 'Chart.js', 'Falha em Chart.' + methodName);",
        "        throw error;",
        "      }",
        "    };",
        "    wrapped.__pregaoWrapped = true;",
        "    target[methodName] = wrapped;",
        "  }",
        "  function wrapChartConstructor(ChartValue) {",
        "    if (!ChartValue || ChartValue.__pregaoWrappedChart) return ChartValue;",
        "    try {",
        "      ['update', 'render', 'draw', 'resize', 'destroy'].forEach(function(methodName) {",
        "        if (ChartValue.prototype) wrapChartMethod(ChartValue.prototype, methodName);",
        "      });",
        "      var proxy = new Proxy(ChartValue, {",
        "        construct: function(target, args, newTarget) {",
        "          try {",
        "            return Reflect.construct(target, args, newTarget);",
        "          } catch (error) {",
        "            sendDetailedError('chart', error, 'Chart.js', 'Falha ao instanciar gráfico');",
        "            throw error;",
        "          }",
        "        },",
        "        apply: function(target, thisArg, args) {",
        "          try {",
        "            return Reflect.apply(target, thisArg, args);",
        "          } catch (error) {",
        "            sendDetailedError('chart', error, 'Chart.js', 'Falha ao executar Chart.js');",
        "            throw error;",
        "          }",
        "        }",
        "      });",
        "      proxy.__pregaoWrappedChart = true;",
        "      return proxy;",
        "    } catch (error) {",
        "      sendDetailedError('chart-hook', error, 'Chart.js', 'Falha ao instrumentar Chart.js');",
        "      return ChartValue;",
        "    }",
        "  }",
        "  (function installChartHook(){",
        "    var chartValue;",
        "    try { chartValue = window.Chart; } catch (e) { chartValue = undefined; }",
        "    try {",
        "      delete window.Chart;",
        "      Object.defineProperty(window, 'Chart', {",
        "        configurable: true,",
        "        enumerable: true,",
        "        get: function() { return chartValue; },",
        "        set: function(value) { chartValue = wrapChartConstructor(value); }",
        "      });",
        "      if (chartValue) {",
        "        window.Chart = chartValue;",
        "      }",
        "    } catch (error) {",
        "      sendDetailedError('chart-hook', error, 'Chart.js', 'Falha ao preparar hook de Chart.js');",
        "    }",
        "  })();",
        "  var originalOnError = window.onerror;",
        "  window.onerror = function(message, source, lineno, colno, error) {",
        "    var stack = error && error.stack ? String(error.stack) : '';",
        "    send({",
        "      errorType: 'runtime',",
        "      message: normalizeRuntimeMessage(String(message || ''), source || '', lineno || 0, colno || 0, stack, null),",
        "      source: source || '',",
        "      lineno: lineno || 0,",
        "      colno: colno || 0,",
        "      stack: stack",
        "    });",
        "    if (typeof originalOnError === 'function') {",
        "      return originalOnError.apply(this, arguments);",
        "    }",
        "    return false;",
        "  };",
        "  window.addEventListener('error', function(event) {",
        "    var target = event && event.target ? event.target : null;",
        "    var message = event && event.message ? event.message : '';",
        "    var source = event && event.filename ? event.filename : '';",
        "    var lineno = event && event.lineno ? event.lineno : 0;",
        "    var colno = event && event.colno ? event.colno : 0;",
        "    var stack = event && event.error && event.error.stack ? String(event.error.stack) : '';",
        "    send({",
        "      errorType: 'runtime',",
        "      message: normalizeRuntimeMessage(message, source, lineno, colno, stack, target),",
        "      source: source,",
        "      lineno: lineno,",
        "      colno: colno,",
        "      stack: stack",
        "    });",
        "  }, true);",
        "  window.addEventListener('unhandledrejection', function(event) {",
        "    var reason = event ? event.reason : null;",
        "    send({",
        "      errorType: 'promise',",
        "      message: reason && reason.message ? String(reason.message) : toText(reason),",
        "      stack: reason && reason.stack ? String(reason.stack) : ''",
        "    });",
        "  });",
        "  if (window.console && typeof window.console.error === 'function') {",
        "    var originalConsoleError = window.console.error.bind(window.console);",
        "    window.console.error = function() {",
        "      var args = Array.prototype.slice.call(arguments).map(toText).join(' | ');",
        "      send({ errorType: 'console', message: args || 'console.error sem detalhes' });",
        "      return originalConsoleError.apply(window.console, arguments);",
        "    };",
        "  }",
        "})();",
        "</scr" + "ipt>"
      ].join("\n");

      return injectScriptIntoHtml(safeHtml, `${readyBootstrap}\n${monitorScript}`);
    },

    resetarEstadoErroPreview() {
      this.dialogErroPreview = false;
      this.corrigindoErroPreview = false;
      this.erroPreviewPendente = null;
      this.detalhesErroPreviewExpandidos = false;
      this.assinaturasErroPreviewJaProcessadas = [];
    },

    normalizarTextoErroPreview(value, maxLength = 1200) {
      const text = value == null ? "" : String(value);
      return text.length > maxLength
        ? `${text.substring(0, maxLength)}...`
        : text;
    },

    criarAssinaturaErroPreview(payload) {
      return [
        payload.errorType || "",
        payload.message || "",
        payload.source || "",
        payload.lineno || 0,
        payload.colno || 0,
      ].join("|");
    },

    criarLinhasErroPreview(erro) {
      if (!erro) return [];

      const linhas = [
        `Tipo: ${erro.errorType || "runtime"}`,
        `Mensagem: ${erro.message || "Erro desconhecido no preview."}`,
      ];

      if (erro.source) linhas.push(`Origem: ${erro.source}`);
      if (erro.lineno) linhas.push(`Linha: ${erro.lineno}`);
      if (erro.colno) linhas.push(`Coluna: ${erro.colno}`);
      if (erro.stack) linhas.push(`Stack: ${erro.stack}`);

      const mensagemNormalizada = String(erro.message || "").trim().toLowerCase();
      if ((mensagemNormalizada === "script error." || mensagemNormalizada === "script error") && !erro.stack) {
        linhas.push("Observação: o navegador expôs apenas um erro genérico de script. Isso normalmente acontece quando a falha vem de script externo sem detalhes completos por restrição de origem/CORS.");
      }

      return linhas;
    },

    montarDetalhesErroPreview(erro) {
      return this.criarLinhasErroPreview(erro).join("\n");
    },

    isErroPreviewGenerico(erro) {
      if (!erro) return true;

      const message = String(erro.message || "").trim().toLowerCase();
      return !message
        || message === "script error."
        || message === "script error";
    },

    calcularPontuacaoErroPreview(erro) {
      if (!erro) return 0;

      let score = 0;
      if (!this.isErroPreviewGenerico(erro)) score += 10;
      if (erro.stack) score += 5;
      if (erro.source) score += 3;
      if (erro.lineno) score += 2;
      if (erro.colno) score += 1;
      if (erro.errorType === "chart") score += 4;
      return score;
    },

    toggleDetalhesErroPreview() {
      this.detalhesErroPreviewExpandidos = !this.detalhesErroPreviewExpandidos;
    },

    handleWindowMessage(event) {
      const payload = event && event.data ? event.data : null;
      if (
        payload
        && payload.tipo === IFRAME_READY_MESSAGE_TYPE
        && payload.origem === "chatIA-preview-ready"
      ) {
        if (!this.$refs.iframePreview || event.source !== this.$refs.iframePreview.contentWindow) return;
        if (payload.token !== this.previewMonitorToken) return;
        this.iframeProntoParaDados = true;
        if (this.internalValue && this.temHtml && this.jsonDados) {
          this.agendarEnvioDadosParaIframe();
        }
        return;
      }

      if (!payload || payload.tipo !== "pregao-preview-error" || payload.origem !== "chatIA-preview") return;
      if (!this.$refs.iframePreview || event.source !== this.$refs.iframePreview.contentWindow) return;
      if (payload.token !== this.previewMonitorToken) return;
      if (!this.internalValue || !this.temHtml) return;
      if (this.digitando || this.corrigindoErroPreview) return;

      const assinatura = this.criarAssinaturaErroPreview(payload);
      if (!assinatura || this.assinaturasErroPreviewJaProcessadas.includes(assinatura)) return;

      this.assinaturasErroPreviewJaProcessadas.push(assinatura);
      const erroNormalizado = {
        errorType: payload.errorType || "runtime",
        message: this.normalizarTextoErroPreview(payload.message || "Erro desconhecido no preview."),
        source: this.normalizarTextoErroPreview(payload.source || "", 300),
        lineno: payload.lineno || 0,
        colno: payload.colno || 0,
        stack: this.normalizarTextoErroPreview(payload.stack || "", 2000),
      };

      if (
        !this.erroPreviewPendente
        || this.calcularPontuacaoErroPreview(erroNormalizado) >= this.calcularPontuacaoErroPreview(this.erroPreviewPendente)
      ) {
        this.erroPreviewPendente = erroNormalizado;
        this.detalhesErroPreviewExpandidos = false;
      }

      if (!this.dialogErroPreview && !this.corrigindoErroPreview) {
        this.dialogErroPreview = true;
      }
    },

    fecharDialogErroPreview() {
      this.dialogErroPreview = false;
      this.detalhesErroPreviewExpandidos = false;
      this.erroPreviewPendente = null;
    },

    async solicitarCorrecaoErroPreview() {
      if (!this.erroPreviewPendente || this.digitando) return;

      const erro = this.erroPreviewPendente;
      const linhasErro = this.criarLinhasErroPreview(erro);
      const mensagemVisivel = "Foi detectado um problema no preview do relatório. Corrija o HTML e gere uma nova versão completa funcionando corretamente com os dados recebidos.";
      const mensagemDiagnostica = [
        "Diagnóstico automático do preview para auxiliar a correção:",
        ...linhasErro.map(linha => `- ${linha}`),
        "Use esse diagnóstico para corrigir o problema e devolver o HTML completo atualizado."
      ].filter(Boolean).join("\n");

      this.corrigindoErroPreview = true;
      this.dialogErroPreview = false;
      this.detalhesErroPreviewExpandidos = false;

      try {
        await this.enviarMensagensUsuario([
          { text: mensagemVisivel },
          { text: mensagemDiagnostica, hideFromChat: true }
        ]);
        this.erroPreviewPendente = null;
      } finally {
        this.corrigindoErroPreview = false;
      }
    },

    extrairHtmlDoTexto(textoTotal) {
      if (!textoTotal) return "";

      const htmlRegex = /```(?:html)?\s+([\s\S]*?)```/gi;
      let lastMatch = null;
      let match;

      while ((match = htmlRegex.exec(textoTotal)) !== null) {
        lastMatch = match[1];
      }

      if (lastMatch) {
        return lastMatch.trim();
      }

      if (textoTotal.includes("<!DOCTYPE") || textoTotal.includes("<html")) {
        const inicio = textoTotal.indexOf("<!DOCTYPE") !== -1
          ? textoTotal.indexOf("<!DOCTYPE")
          : textoTotal.indexOf("<html");
        let textoHTML = textoTotal.substring(inicio);

        if (textoHTML.includes("</html>")) {
          textoHTML = `${textoHTML.split("</html>")[0]}</html>`;
        }

        return textoHTML.trim();
      }

      return "";
    },

    textoParaHtml(text, isHtmlContext) {
      if (!text) return "";
      if (isHtmlContext) return "<em>(contexto HTML anexado)</em>";

      let textoConvertido = text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");

      textoConvertido = textoConvertido.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
      textoConvertido = textoConvertido.replace(/\*(.*?)\*/g, "<em>$1</em>");
      textoConvertido = textoConvertido.replace(/\n/g, "<br>");

      return textoConvertido;
    },

    iniciarSessao() {
      this.messages = [];
      this.versions = [];
      this.currentVersionId = null;
      this.inputTexto = "";
      this.limparImagensPendentes();
      this.htmlExtraido = "";
      this.abortarStreaming();
      this.nextMessageId = 1;
      this.nextVersionId = 1;
      this.iframeProntoParaDados = false;
      this.resetarEstadoErroPreview();

      if (this.modo === "editar" && this.htmlInicial) {
        this.definirHtmlExtraido(this.htmlInicial);

        this.messages.push(this.criarMensagem({
          role: "model",
          text: "Oi! Como posso te ajudar com esse relatório?",
          isLocal: true,
        }));

        this.registrarVersao({
          html: this.htmlInicial,
          sourceMessageId: null,
        });
      } else {
        this.messages.push(this.criarMensagem({
          role: "model",
          text: "Olá! Sou o assistente de criação de relatórios para o sistema Pregão. Como posso te ajudar?",
          isLocal: true,
        }));
      }

      this.scrollBottom();
      this.$nextTick(() => {
        this.conectarListenerPaste();
      });
    },

    fechar() {
      this.abortarStreaming();
      this.resetarEstadoErroPreview();
      this.internalValue = false;
    },

    handleViewportChange() {
      if (!this.internalValue || !this.temHtml || !this.iframeCarregado || !this.iframeProntoParaDados) return;
      this.agendarEnvioDadosParaIframe();
    },

    handleKeydown(e) {
      if (e.key === "Enter" && !e.shiftKey && this.canSendMessage) {
        e.preventDefault();
        this.enviarMensagem();
      }
    },

    async enviarMensagensUsuario(messageConfigs) {
      if (!Array.isArray(messageConfigs) || messageConfigs.length === 0 || this.digitando) return;

      const htmlAntesMensagem = this.normalizarHtmlSnapshot(
        this.currentVersion ? this.currentVersion.html : this.htmlExtraido
      );

      const mensagensUsuario = messageConfigs.map(config => this.criarMensagem({
        role: "user",
        text: config.text || "",
        parts: this.criarPartsMensagemUsuario(config.text || "", config.images || []),
        isLocal: !!config.isLocal,
        isHtmlContext: !!config.isHtmlContext,
        hideFromChat: !!config.hideFromChat,
      }));

      this.messages.push(...mensagensUsuario);
      this.scrollBottom();

      const historicoMensagens = this.messages.slice(0, this.messages.length - mensagensUsuario.length);
      const msgsParaEnvio = this.prepararMensagensParaAPI(historicoMensagens, mensagensUsuario);

      const aiMsgObj = this.criarMensagem({ role: "model" });
      this.messages.push(aiMsgObj);
      this.digitando = true;

      this.abortarStreaming();
      this.abortController = new AbortController();

      try {
        const response = await fetch(`${urlApi}/analytics/ai/chat`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: axios.defaults.headers.common.Authorization
          },
          body: JSON.stringify({
            messages: msgsParaEnvio,
            config: { temperature: this.temperature }
          }),
          signal: this.abortController.signal
        });

        if (!response.ok || !response.body) {
          let errorMessage = `Erro: HTTP ${response.status}`;
          let errorDetails = "";

          try {
            errorDetails = await response.text();
          } catch (readError) {
            errorDetails = "";
          }

          if (response.status === 413) {
            errorMessage = "O anexo enviado é grande demais para o chat. Tente uma imagem menor ou com menos detalhes.";
          } else if (errorDetails) {
            errorMessage = `${errorMessage} - ${errorDetails.substring(0, 200)}`;
          }

          const httpError = new Error(errorMessage);
          httpError.status = response.status;
          throw httpError;
        }

        let fullResponseText = "";
        const responseParts = [];
        const reader = response.body.getReader();
        const decoder = new TextDecoder("utf-8");
        let buffer = "";
        let lendo = true;

        while (lendo) {
          const { done, value } = await reader.read();
          if (done) {
            lendo = false;
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          buffer = buffer.replace(/\r\n/g, "\n");

          let breakIdx;
          while ((breakIdx = buffer.indexOf("\n\n")) >= 0) {
            const bloco = buffer.substring(0, breakIdx).trim();
            buffer = buffer.substring(breakIdx + 2);

            if (!bloco) continue;

            if (bloco.includes("event: error")) {
              const errMatch = bloco.match(/data:\s*(.*)/);
              let errorMsg = "Erro na IA.";
              if (errMatch) {
                try {
                  errorMsg = JSON.parse(errMatch[1]).error;
                } catch (e) {
                  console.warn("Erro ao parsear erro SSE", e);
                }
              }
              throw new Error(errorMsg);
            }

            const dataMatch = bloco.match(/^data:\s*(.+)$/m);
            if (!dataMatch) continue;

            try {
              const dataJson = JSON.parse(dataMatch[1]);
              const parts = dataJson && dataJson.candidates && dataJson.candidates[0]
                && dataJson.candidates[0].content
                && Array.isArray(dataJson.candidates[0].content.parts)
                ? dataJson.candidates[0].content.parts
                : [];

              if (!parts.length) continue;

              const partesClonadas = this.clonarMensagens(parts);
              responseParts.push(...partesClonadas);

              const textoNovo = partesClonadas
                .filter(part => part && typeof part.text === "string")
                .map(part => part.text)
                .join("");

              if (textoNovo) {
                fullResponseText += textoNovo;
                this.$set(aiMsgObj, "parts", [{ text: fullResponseText }]);
                this.scrollBottom();
              }
            } catch (e) {
              console.debug("Chunk JSON ignorado:", dataMatch[1].substring(0, 80), e.message);
            }
          }
        }

        if (responseParts.length) {
          this.$set(aiMsgObj, "parts", responseParts);
        }

        const htmlGerado = this.extrairHTML(fullResponseText);
        if (htmlGerado) {
          this.registrarVersao({
            html: htmlGerado,
            sourceMessageId: aiMsgObj.id,
          });
        } else if (htmlAntesMensagem) {
          this.definirHtmlExtraido(htmlAntesMensagem);
        }
      } catch (err) {
        if (err.name !== "AbortError") {
          mensagensUsuario.forEach(message => {
            message.isLocal = true;
          });
          showError(err, "Falha na comunicação com IA.");
          this.$set(aiMsgObj.parts, 0, { text: "*(Falhou na geração da resposta)*" });
          this.definirHtmlExtraido(htmlAntesMensagem);
        }
      } finally {
        this.digitando = false;
        this.abortController = null;
        this.scrollBottom();
      }
    },

    async enviarMensagem() {
      if (!this.canSendMessage || this.digitando) return;

      const userMessage = this.inputTexto.trim();
      const imagens = this.imagensPendentes.map(image => ({ ...image }));
      this.inputTexto = "";
      this.limparImagensPendentes();

      await this.enviarMensagensUsuario([
        { text: userMessage, images: imagens }
      ]);
    },

    extrairHTML(textoTotal) {
      const htmlExtraido = this.extrairHtmlDoTexto(textoTotal);

      if (htmlExtraido) {
        this.definirHtmlExtraido(htmlExtraido);
      }

      return htmlExtraido;
    },

    forcarAtualizarPreview() {
      let htmlParaRenderizar = this.currentVersion && this.currentVersion.html
        ? this.currentVersion.html
        : this.htmlExtraido;

      if (this.currentVersion && this.currentVersion.html) {
        this.definirHtmlExtraido(this.currentVersion.html);
      } else {
        for (let i = this.messages.length - 1; i >= 0; i--) {
          const text = this.obterTextoMensagem(this.messages[i]);
          if (this.messages[i].role === "model" && text) {
            htmlParaRenderizar = this.extrairHTML(text) || htmlParaRenderizar;
            break;
          }
        }
      }

      if (!htmlParaRenderizar) return;

      this.resetarEstadoErroPreview();
      clearIframePayloadSyncTimer(this, "reenvioPreviewTimer", "reenvioPreviewSequencia");

      if (this.htmlExtraido !== htmlParaRenderizar) {
        this.definirHtmlExtraido(htmlParaRenderizar);
        return;
      }

      if (this.iframeCarregado && this.iframeProntoParaDados) {
        this.enviarDadosParaIframe();
      }
    },

    criarNomeDownloadRelatorio() {
      if (this.modo === "editar" && this.nomeOriginal) {
        return this.nomeOriginal;
      }

      return this.criarNomeInternoRelatorio().replace("relatorio_", "relatorio_preview_");
    },

    baixarHtmlAtual() {
      if (!this.temHtml) return;

      const blob = new Blob([this.htmlExtraido], { type: "text/html;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = this.criarNomeDownloadRelatorio();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },

    onIframeLoad() {
      this.iframeCarregado = true;
      this.iframeProntoParaDados = false;
    },

    agendarEnvioDadosParaIframe() {
      scheduleIframePayloadSync(
        this,
        "reenvioPreviewTimer",
        () => {
          this.enviarDadosParaIframe();
        },
        {
          tokenField: "reenvioPreviewSequencia",
          iframeResolver: () => this.$refs.iframePreview,
        }
      );
    },

    enviarDadosParaIframe() {
      if (
        !this.$refs.iframePreview
        || !this.htmlExtraido
        || !this.jsonDados
        || !this.iframeCarregado
        || !this.iframeProntoParaDados
      ) return;
      const iframe = this.$refs.iframePreview;
      if (iframe.contentWindow) {
        iframe.contentWindow.postMessage({
          tipo: "pregao-dados",
          dados: this.jsonDados
        }, "*");
      }
    },

    scrollBottom() {
      this.$nextTick(() => {
        if (this.$refs.messagesContainer) {
          this.$refs.messagesContainer.scrollTop = this.$refs.messagesContainer.scrollHeight;
        }
      });
    },

    abortarStreaming() {
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
        this.digitando = false;
      }
    },

    criarNomeInternoRelatorio() {
      const agora = new Date();
      const pad = (valor, tamanho = 2) => String(valor).padStart(tamanho, "0");
      return `relatorio_${agora.getFullYear()}${pad(agora.getMonth() + 1)}${pad(agora.getDate())}_${pad(agora.getHours())}${pad(agora.getMinutes())}${pad(agora.getSeconds())}_${pad(agora.getMilliseconds(), 3)}.html`;
    },

    async finalizarESalvar() {
      this.salvando = true;
      const nomeGerado = this.modo === "editar" ? this.nomeOriginal : this.criarNomeInternoRelatorio();
      const arquivoBlob = new Blob([this.htmlExtraido], { type: "text/html;charset=utf-8" });

      try {
        if (this.modo === "editar") {
          await axios.delete(`${urlApi}/analytics/relatorios/${this.nomeOriginal}`);
        }

        const fd = new FormData();
        fd.append("nome", nomeGerado);
        fd.append("arquivo", arquivoBlob, nomeGerado);

        await axios.post(`${urlApi}/analytics/relatorios`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        this.$toasted.global.defaultSuccess({ msg: "Relatório gerado por IA salvo com sucesso!" });
        this.$emit("salvo", nomeGerado);
        this.fechar();
      } catch (err) {
        showError(err, "Falha ao salvar relatório gerado.");
      } finally {
        this.salvando = false;
      }
    }
  }
};
</script>

<style scoped>
.chat-ia-container {
  background-color: #f5f7fa;
  height: 100vh;
  overflow: hidden;
}

.chat-ia-toolbar {
  min-height: 56px !important;
  max-height: 56px !important;
}

.chat-ia-toolbar>>>.v-toolbar__content {
  min-height: 56px !important;
  max-height: 56px !important;
  padding: 0 12px;
}

.toolbar-configs {
  gap: 8px;
}

.ia-model-select {
  font-size: 13px;
}

.ia-model-select>>>.v-input__slot {
  min-height: 32px !important;
}

.temp-control {
  min-width: 180px;
  gap: 8px;
}

.temp-label {
  white-space: nowrap;
}

.temp-slider {
  min-width: 100px;
}

.temp-slider>>>.v-slider__thumb {
  cursor: pointer;
}

.panels-wrapper {
  overflow: hidden;
}

.chat-panel {
  width: 30%;
  border-right: 1px solid #e0e0e0;
  background-color: #ffffff;
  min-width: 300px;
}

.messages-container {
  overflow-y: auto;
  scroll-behavior: smooth;
}

.message-wrapper {
  max-width: 100%;
}

.message-content {
  max-width: 90%;
}

.avatar {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
}

.ai-avatar {
  background-color: #e0f2f1;
}

.user-avatar {
  background-color: #0b7285;
}

.message-bubble {
  border-radius: 12px;
  line-height: 1.5;
  font-size: 14px;
  word-wrap: break-word;
  max-width: 100%;
}

.ai-bubble {
  background-color: #f8f9fa;
  border: 1px solid #e8e8e8;
  border-top-left-radius: 2px;
  color: #333;
}

.user-bubble {
  background-color: #e3f4f6;
  border-top-right-radius: 2px;
  color: #0c4d5c;
}

.message-text {
  white-space: pre-wrap;
  word-break: break-word;
}

.message-images {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(92px, 92px));
  gap: 8px;
}

.message-image-button,
.pending-image-button {
  display: block;
  border: none;
  padding: 0;
  background: transparent;
  cursor: pointer;
}

.message-image-thumb,
.pending-image-thumb {
  width: 92px;
  height: 92px;
  border-radius: 10px;
  object-fit: cover;
  border: 1px solid #d8dee6;
  background-color: #f5f7fa;
}

.typing-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  margin-right: 4px;
  border-radius: 50%;
  background-color: #90a4ae;
  animation: typing 1.4s infinite;
}

.typing-dot:nth-child(2) {
  animation-delay: 0.2s;
}

.typing-dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes typing {

  0%,
  100% {
    transform: translateY(0);
    opacity: 0.5;
  }

  50% {
    transform: translateY(-4px);
    opacity: 1;
  }
}

.preview-panel {
  width: 70%;
  background-color: #f5f7fa;
  flex-grow: 1;
}

.preview-header {
  background-color: #eeeeee;
  border-bottom: 1px solid #e0e0e0;
  min-height: 40px;
}

.preview-content {
  overflow: hidden;
}

.empty-preview {
  height: 100%;
}

.analytics-iframe {
  width: 100%;
  height: 100%;
  border-radius: 8px;
  background-color: #fff;
}

.actions-panel {
  background-color: #ffffff;
  border-top: 1px solid #e0e0e0;
}

.pending-images-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(112px, 112px));
  gap: 12px;
}

.pending-image-card {
  width: 112px;
}

.pending-image-name {
  max-width: 84px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.version-controls {
  min-width: 180px;
}

.version-status {
  min-width: 110px;
  text-align: center;
}

.image-preview-dialog {
  overflow: hidden;
}

.image-preview-body {
  min-height: 360px;
  max-height: 75vh;
  background-color: #101820;
}

.image-preview-full {
  max-width: 100%;
  max-height: 68vh;
  object-fit: contain;
}

.erro-preview-toggle {
  min-height: 28px;
}

.erro-preview-box {
  max-height: 220px;
  overflow-y: auto;
  border: 1px solid #d8dee6;
  border-radius: 8px;
  background-color: #f8fafc;
}

.erro-preview-text {
  margin: 0;
  padding: 12px;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 12px;
  line-height: 1.5;
  color: #334155;
  font-family: Consolas, "Courier New", monospace;
}

.border-bottom {
  border-bottom: 1px solid #e0e0e0;
}
</style>
